import React, { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import {
  SpendTrendChart, SecurityCoverageHeatmap, CategorySpendBars, WidgetStatus, Sparkline,
  CeilingGauge, chartPalette, chartOtherColor } from '@techstack/ui';
import {
  fiscalYearLabelToRange, getFiscalCalendar, DEFAULT_FISCAL_CALENDAR_KEY, fiscalYearRange,
  formatFiscalYear, fiscalYearOf } from '@techstack/core';
import apiClient from '../../lib/apiClient';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';
import { useTimeFilterContext } from '../../contexts/TimeFilterContext';
import FiscalBasisToggle from '../FiscalBasisToggle/FiscalBasisToggle';
import { fmtMoney, accountKey, isPartialFy, footprintWindow, fyEndYear, windowCoversFiscalYear, fiscalSpanLabel } from './accountsFormat';
import AccountsOverviewIntel from './AccountsOverviewIntel';
import { PurchasingAuthorityCard } from '../AccountWorkspace/PurchasingAuthority';
import SharedTransactionsDrilldownModal from '../shared/SharedTransactionsDrilldownModal';
import PoDetailModal from '../PoDetailModal/PoDetailModal';

/*
 * AccountsDashTab — the GAS Customer Overview's charts, on /accounts.
 *
 * Ladder S1 in docs/ACCOUNTS-GAS-PARITY.md. Charts come from @techstack/ui,
 * the same components GAS renders, so the two hosts draw identical graphs
 * (frontend invariant 5) rather than two drifting implementations.
 *
 * The previous version said the trajectory chart was blocked on a missing
 * endpoint. It was not: the endpoints existed, but /api/v1/accounts/:account/
 * evidence was API-key-only and TekStak's apiClient sends only a JWT, so the
 * call 401'd. That route now uses the dual-path keyScope middleware (S0).
 *
 * Data:
 *   stat cards   GET /api/gap-analysis/coverage?departmentId=N
 *   agency trend GET /api/v1/accounts/:account/evidence          (spendTrend)
 *   PANW trend   GET /api/vendor-analysis/Palo Alto Networks/trajectory
 *   security     GET /api/vendor-analysis/security-coverage/vendor-breakdown
 *
 * Drill-down (GAS: "click a bar for transactions") is deliberately NOT wired
 * yet — the agency-wide bar needs a dept-scoped transactions endpoint that is
 * still v1-only, and half-clickable charts are worse than none. S1b.
 */

const PANW = 'Palo Alto Networks';
/** GAS caps the Overview heatmap at its top 18 vendors by spend; the full
 *  breakdown lives on the Zero Trust tab. Same cap here so the two match. */
const HEATMAP_VENDOR_CAP = 18;

/* Categorical hues for the partner stack. Still hex and still byte-identical
   to GAS's — that reasoning was right and is now enforced by both apps reading
   ONE array instead of three files agreeing by hand. A partner's colour
   encodes identity, not theme, so it must not move when the viewer flips to
   light mode. "Other" and unattributed spend stay grey: they are a residual,
   not a partner. See @techstack/ui tokens.ts. */
const PARTNER_PALETTE = chartPalette;
const PARTNER_OTHER_COLOR = chartOtherColor;
const PARTNER_RESIDUALS = new Set(['Other', 'Unknown/Unattributed']);



/** Oldest → newest regardless of how the API sorted it: evidence returns
 *  newest-first, and the chart reads left to right. */
const byFyAsc = (a, b) => String(a.fy).localeCompare(String(b.fy));

/** A panel whose query failed says so in place; it never fakes an empty chart. */
function PanelError({ message }) {
  return (
    <div className="accounts-error">
      <AlertCircle size={14} /> {message}
    </div>
  );
}

/**
 * `section` selects ONE panel, so the account board can place the stat cards,
 * the two trajectories and the security heatmap as independent widgets while
 * they keep sharing this file's queries and — the part that matters — its
 * drill wiring. Omitted renders the whole stack, unchanged.
 *
 * Every panel mounting the same react-query keys is deliberate: the queries
 * dedupe, so four widgets cost one fetch each, not four.
 */
/*
 * A year-over-year change, stated so it can be believed. A percentage from a
 * near-zero base is arithmetic, not information: CSAC's IT spend read
 * "▲ 57288% vs FY24-25" because FY24-25 held a few thousand dollars. Below a
 * floor (1% of the biggest year, or $10K) the change is given in dollars with
 * the base named; above it, the percentage. `null` prior means "no year to
 * compare against".
 *
 * BOTH YEARS ARE NAMED, and that is the whole point of `latestLabel`.
 *
 * While a year is in progress the card's headline is the OPEN year and this
 * delta is the two CLOSED years before it — so the figure and the change are
 * about different years, and a chip reading only "vs FY25" invites the reader
 * to attach it to the number above. Measured on State Controller 2026-09-17:
 * the Palo Alto card showed "$474K · FY27 to date" over "▼ 92% vs FY25" while
 * Palo Alto had in fact gone $757K (FY25) -> $64K (FY26) -> $474K (FY27 to
 * date). The red arrow was true of FY26 and read as a collapse in FY27, which
 * was a SEVENFOLD RECOVERY. Naming the measured year is what stops that.
 */
function describeDelta(latest, prior, priorLabel, seriesMax, fmt, latestLabel = null) {
  const at = (body) => (latestLabel ? `${latestLabel} ${body}` : body);
  if (latest == null || prior == null || !priorLabel) return null;
  const up = latest >= prior;
  const floor = Math.max((seriesMax || 0) * 0.01, 10000);
  const inLatest = latestLabel ? ` in ${latestLabel}` : '';
  if (prior <= 0) {
    return latest > 0
      ? { up: true, text: at(`new · nothing in ${priorLabel}`), title: `${fmt(latest)}${inLatest} against $0 in ${priorLabel}` }
      : { up: false, text: at(`nothing · nor in ${priorLabel}`), title: `$0 in both years` };
  }
  if (prior < floor) {
    return { up, text: at(`${up ? '▲' : '▼'} ${fmt(Math.abs(latest - prior))} vs ${priorLabel} (from ${fmt(prior)})`), title: `${fmt(latest)}${inLatest} against ${fmt(prior)} in ${priorLabel} — too small a base for a percentage` };
  }
  const pct = ((latest - prior) / prior) * 100;
  return { up, text: at(`${up ? '▲' : '▼'} ${Math.abs(pct).toFixed(0)}% vs ${priorLabel}`), title: `${fmt(latest)}${inLatest} against ${fmt(prior)} in ${priorLabel}` };
}

export default function AccountsDashTab({ selectedDepartment, section = null, fill = false }) {
  const wants = (name) => section === null || section === name;
  const departmentId = selectedDepartment?.department_id;
  const account = accountKey(selectedDepartment);
  // The VIEWER's fiscal calendar, never the viewed vendor's (invariant 8).
  const { basis, isDefaultBasis, calendar: fiscalCalendar } = useFiscalBasisContext();
  // The agency-wide series is ALWAYS bucketed on the California state
  // calendar — mv_vendor_analysis_facts is baked to that boundary — whatever
  // the toggle says. Its drill must use that calendar, not the active one, or
  // the modal opens on a window the bar never represented.
  const stateCal = getFiscalCalendar(DEFAULT_FISCAL_CALENDAR_KEY);
  const activeCal = getFiscalCalendar(basis);
  const [drill, setDrill] = useState(null);
  const [poDoc, setPoDoc] = useState(null);
  // Canonical time axis for base-table endpoints (invariant 7), and what GAS
  // binds on this same card.
  // `timePeriod` is the topbar's own vocabulary ('3y'), which is exactly what
  // the caption should echo — a reader checking the widget against the control
  // should see the same token in both places.
  const { dateRange, timePeriod } = useTimeFilterContext();

  const coverageQuery = useQuery({
    queryKey: ['accounts-coverage', departmentId],
    queryFn: () => apiClient.get('/api/gap-analysis/coverage', {
      params: { departmentId, limit: 1 },
    }),
    enabled: !!departmentId,
    staleTime: 60_000,
  });

  // Agency-wide spend. Always the CALIFORNIA STATE fiscal year — the matview
  // it reads is baked to that boundary — so this chart names its calendar
  // rather than silently sharing an axis with the PANW-basis chart.
  const trendQuery = useQuery({
    queryKey: ['accounts-evidence', account],
    queryFn: () => apiClient.get(`/api/v1/accounts/${encodeURIComponent(account)}/evidence`),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const panwQuery = useQuery({
    // basis is in the key: flipping the toggle must refetch, not re-label
    // last calendar's buckets (invariant 8).
    queryKey: ['accounts-panw-trajectory', departmentId, basis],
    queryFn: () => apiClient.get(
      `/api/vendor-analysis/${encodeURIComponent(PANW)}/trajectory`,
      {
        params: {
          department_ids: departmentId,
          // Omitted on the default basis so the request stays byte-identical
          // to what it was before the toggle existed.
          ...(isDefaultBasis ? {} : { basis }),
        },
      },
    ),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });

  // Palo Alto PRODUCT by fiscal year — the footprint widget's own query and
  // key, so the Key numbers figure and the heatmap under it are one fetch and
  // one truth. (The trajectory endpoint reads the supplier path, on which
  // Palo Alto never appears in California procurement — it said "no Palo Alto
  // spend on file" above a heatmap full of it. Ryan, 2026-09-14.)
  const panwWindow = footprintWindow(dateRange);
  const footprintQuery = useQuery({
    queryKey: ['accounts-panw-footprint', account, basis,
      panwWindow.startDate, panwWindow.endDate],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/panw-footprint`,
      {
        params: {
          ...(isDefaultBasis ? {} : { basis }),
          // The footprint honoured NO time filter: it asked all-time while the
          // topbar said 3y, so the heatmap drew five fiscal years ending two
          // years before the Key numbers cards beside it. Both ends go, as
          // real dates — see footprintWindow for why not `sinceYear`.
          ...(panwWindow.startDate ? { startDate: panwWindow.startDate } : {}),
          ...(panwWindow.endDate ? { endDate: panwWindow.endDate } : {}),
        },
      },
    ),
    enabled: !!account && (section === null || section === 'stats'),
    staleTime: 5 * 60 * 1000,
  });

  /*
   * Our share of the agency's security spend over five fiscal years, and the
   * agency's security spend by year to draw the trajectory (Ryan, 2026-09-14).
   * Five years because a Palo Alto EA is a 3-5 year term booked in a single
   * year: CDT's yearly share reads 37, 12, 7, 3 then 82 per cent, and none of
   * those five numbers describes the relationship.
   */
  /*
   * Who the agency's spend went TO, per year. The bump-chart widget asks for
   * exactly this, so the two share a query key and cost one fetch between
   * them (frontend invariant: keys dedupe, panels do not refetch).
   *
   * THE KEY HAS TO CARRY THE PARAMS, not just the account. Sharing a key while
   * sending a different URL does not cost one fetch — it silently serves one
   * component the other's data, whichever mounted first. That was live here
   * and invisible only by luck: this asked for no window at all and the widget
   * asked for `years=5`, which is exactly what the endpoint defaults to, so
   * the two requests happened to agree. The moment the widget started taking
   * the topbar's window — which it now does, because it always should have —
   * this panel would have begun rendering whatever span the board was on
   * while its own caption named a different one.
   *
   * `null` is this panel's window on purpose: the endpoint's own default span,
   * deliberately not the time filter. The caption says which years came back.
   */
  const mixQuery = useQuery({
    queryKey: ['accounts-vendor-trajectory', account, null, 6],
    queryFn: () => apiClient.get(`/api/v1/accounts/${encodeURIComponent(account)}/vendor-trajectory`, { params: { topN: 6 } }),
    enabled: !!account && (section === null || section === 'spend'),
    staleTime: 5 * 60 * 1000,
  });

  const shareQuery = useQuery({
    queryKey: ['accounts-security-share', account, basis],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/security-share`,
      { params: isDefaultBasis ? {} : { basis } },
    ),
    enabled: !!account && (section === null || section === 'stats'),
    staleTime: 5 * 60 * 1000,
  });

  // PO-LEVEL totals for the ceiling gauge (W6). Enabled only for the section
  // that draws it: this is a base-table aggregate, and the stacked tab would
  // otherwise pay for it on every account open whether or not anyone scrolls
  // to the card.
  const poQuery = useQuery({
    queryKey: ['accounts-po-totals', account],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/purchase-orders`,
      { params: { limit: 200 } },
    ),
    enabled: !!account && (section === null || section === 'purchasing-authority'),
    staleTime: 15 * 60 * 1000,
  });

  // S3: the same DGS tier card the AccountWorkspace and GAS both show.
  const paQuery = useQuery({
    queryKey: ['accounts-purchasing-authority', account],
    queryFn: () => apiClient.get(
      `/api/v1/accounts/${encodeURIComponent(account)}/purchasing-authority`,
    ),
    enabled: !!account,
    staleTime: 60 * 60 * 1000,
  });

  // Which resellers carry Palo Alto into this account, split by category.
  const partnersQuery = useQuery({
    queryKey: ['accounts-partners-by-category', departmentId, dateRange?.startDate, dateRange?.endDate],
    queryFn: () => apiClient.get(
      `/api/vendor-analysis/${encodeURIComponent(PANW)}/partners-by-category`,
      {
        params: {
          department_ids: departmentId,
          // Absent on an all-time window, which the endpoint accepts.
          ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
          ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
        },
      },
    ),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });

  /*
   * SECURITY COVERAGE HONOURS THE PAGE'S TIME FILTER.
   *
   * Ryan: "This is pulling up Cisco PO's from 2018. I can't have the security
   * coverage be all historical."
   *
   * He was right, and the fix is not a new control. This widget was the only
   * agency-scoped, time-relevant query on the page NOT binding the shared
   * filter — nine of its neighbours already do — so it silently showed all
   * time while the topbar said 3y. A 2018 purchase says nothing about what an
   * agency runs today, and eight years of accumulated spend outranks three
   * years of current spend in every cell.
   *
   * A second control in the widget's corner was the other option and is worse:
   * the frontend invariant in apps/frontend/CLAUDE.md (#7) says every
   * agency-specific, time-relevant view reads the window from TimeFilterContext
   * rather than carrying its own, precisely so the page cannot end up saying 3y
   * at the top and 5y in the middle with nothing reconciling them. The topbar
   * already offers 1y / 3y / 5y and already defaults to 3y, which is the
   * default asked for.
   *
   * The dates belong in the KEY as well as the params, or a flip re-renders
   * against a cached answer for the old window.
   */
  const securityQuery = useQuery({
    queryKey: ['accounts-security-coverage', departmentId, dateRange?.startDate, dateRange?.endDate],
    queryFn: () => apiClient.get('/api/vendor-analysis/security-coverage/vendor-breakdown', {
      params: {
        department_id: departmentId,
        ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
      },
    }),
    enabled: !!departmentId,
    staleTime: 5 * 60 * 1000,
  });

  const spendTrend = useMemo(() => {
    const rows = trendQuery.data?.spendTrend || [];
    return rows.filter((x) => x && x.total != null).sort(byFyAsc);
  }, [trendQuery.data]);

  const panTrend = useMemo(() => {
    const yearly = panwQuery.data?.yearly || [];
    let rows = yearly
      .filter((y) => y && y.fy != null)
      .map((y) => ({ fy: y.fy, total: Number(y.total_spend) || 0 }))
      .sort(byFyAsc);
    // On the default basis both series sit on the same calendar, so align
    // PANW to the agency axis (zero-filling gaps) and the two charts compare
    // bar for bar. On any other basis the labels mean different windows, and
    // zero-filling one onto the other would restate PANW spend as state
    // years — so each chart keeps its own bars and names its own calendar.
    if (rows.length && spendTrend.length && isDefaultBasis) {
      const byFy = {};
      rows.forEach((x) => { byFy[x.fy] = x.total; });
      rows = spendTrend.map((x) => ({ fy: x.fy, total: byFy[x.fy] || 0 }));
    }
    return rows;
  }, [panwQuery.data, spendTrend, isDefaultBasis]);

  // Top vendors by spend, matching GAS's Overview cap.
  const security = useMemo(() => {
    const d = securityQuery.data;
    if (!d?.rows?.length || !d?.cells?.length) return null;
    const spendByRow = {};
    d.cells.forEach((c) => { spendByRow[c.rowId] = (spendByRow[c.rowId] || 0) + (c.spend || 0); });
    const topIds = new Set(
      Object.entries(spendByRow)
        .sort((a, b) => b[1] - a[1])
        .slice(0, HEATMAP_VENDOR_CAP)
        .map(([id]) => id),
    );
    const keep = (id) => topIds.has(String(id)) || topIds.has(id);
    const rows = d.rows.filter((r) => keep(r.id));
    const cells = d.cells.filter((c) => keep(c.rowId));
    if (!rows.length) return null;
    return {
      rows,
      columns: d.columns,
      cells,
      maxCell: cells.reduce((m, c) => Math.max(m, c.spend || 0), 0),
    };
  }, [securityQuery.data]);

  // Agency bar → every classified tech transaction for the dept in that FY.
  // /procurement/transactions mirrors the matview's WHERE, so the bar total
  // and the list total reconcile.
  const drillAgencyBar = useCallback((point) => {
    const range = fiscalYearLabelToRange(point?.fy, stateCal);
    if (!range || !departmentId) return;
    setDrill({
      title: `All vendors · ${point.fy}`,
      contextLabel: `${account} · ${range.startDate} – ${range.endDate}`,
      expected: point.total,
      queryKey: ['accounts-drill-agency', departmentId, point.fy],
      path: '/api/v1/procurement/transactions',
      params: { dept: departmentId, startDate: range.startDate, endDate: range.endDate, limit: 500 },
    });
  }, [departmentId, account, stateCal]);

  // PANW bar → Palo Alto only, on the ACTIVE calendar, since that series was
  // bucketed with the toggle's basis.
  const drillPanwBar = useCallback((point) => {
    const range = fiscalYearLabelToRange(point?.fy, activeCal);
    if (!range || !departmentId) return;
    setDrill({
      title: `${PANW} · ${point.fy}`,
      contextLabel: `${account} · ${range.startDate} – ${range.endDate}`,
      expected: point.total,
      queryKey: ['accounts-drill-panw', departmentId, point.fy, basis],
      path: `/api/vendor-analysis/${encodeURIComponent(PANW)}/transactions`,
      // This route takes `department` (a single id/acronym/name), NOT the
      // `department_ids` list the trajectory endpoint takes.
      params: {
        department: departmentId,
        startDate: range.startDate,
        endDate: range.endDate,
        limit: 500,
      },
    });
  }, [departmentId, account, activeCal, basis]);

  // Heatmap cell → that vendor's lines in that security category. The
  // breakdown behind the matrix is all-time, so the drill carries NO dates —
  // otherwise the cell total and the list total would not reconcile.
  const drillSecurityCell = useCallback((cell, row, col) => {
    if (!departmentId || !row?.label) return;
    setDrill({
      title: `${row.label} · ${col?.label || col?.id}`,
      /*
       * The drill takes the CHART's window, not "all time". The modal promises
       * "the list below should reconcile to it", and a windowed cell opening an
       * all-time list breaks that promise in the other direction from the
       * confidence-floor bug — same contract, opposite cause.
       */
      contextLabel: dateRange?.startDate
        ? `${account} · ${dateRange.startDate} – ${dateRange.endDate}`
        : `${account} · all time`,
      expected: cell?.spend,
      queryKey: ['accounts-drill-security', departmentId, row.label, col?.id,
        dateRange?.startDate, dateRange?.endDate],
      path: `/api/vendor-analysis/${encodeURIComponent(row.label)}/transactions`,
      // classifiedVendor repeats the row label on purpose: the path param
      // picks the canonical vendor, classifiedVendor scopes to the lines the
      // matrix actually counted for that cell.
      params: {
        department: departmentId,
        classifiedVendor: row.label,
        securityCategory: col?.id,
        ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
        /*
         * WITHOUT THIS THE DRILL CONTRADICTS THE CHART IT CAME FROM.
         *
         * Ryan, on CrowdStrike x SIEM at CSAC: the cell reads $98K, the modal
         * says "0 rows · $0" under a footer promising "the list below should
         * reconcile to it".
         *
         * The security-coverage aggregation applies NO confidence filter —
         * `spm.confidence` only picks which mapping row wins, it never drops a
         * line. The transactions endpoint defaults the other way and floors at
         * component_confidence >= 0.75, so every line the matrix counted below
         * that floor vanishes on the way down. CrowdStrike's $98K at this
         * agency is all below it, which turns a real cell into an empty list.
         *
         * services/vendorAnalysis/transactions.js says so in as many words:
         * "a segment drill-down must default to the 'all' set (strict=false,
         * includeLowConfidence=true) to reconcile exactly." This drill was the
         * one that never passed it.
         */
        includeLowConfidence: true,
        limit: 500,
      },
    });
  }, [departmentId, account, dateRange]);

  const partners = useMemo(() => {
    const d = partnersQuery.data;
    if (!d?.categories?.length) return null;
    const topVendors = (d.partners || []).map((x) => x.partner);
    const colorMap = {};
    let ci = 0;
    topVendors.forEach((name) => {
      if (PARTNER_RESIDUALS.has(name)) {
        colorMap[name] = PARTNER_OTHER_COLOR;
      } else {
        colorMap[name] = PARTNER_PALETTE[ci % PARTNER_PALETTE.length];
        ci += 1;
      }
    });
    const chartData = d.categories.map((c) => ({
      categoryId: c.category,
      category: c.category,
      totalSpend: c.total_spend,
      totalSpendFormatted: c.total_spend_formatted,
      vendors: Object.fromEntries((c.partners || []).map((x) => [x.partner, x.spend])),
    }));
    return { chartData, topVendors, colorMap };
  }, [partnersQuery.data]);

  // Segment → that reseller's Palo Alto lines in that category. "Other" and
  // unattributed are residuals, not suppliers, so they drill the category
  // without a supplier filter and carry no expected figure to reconcile to.
  // Every hook has to sit above the coverage early return below — this one is
  // the first hook added after it, and eslint caught it as a rules-of-hooks
  // error rather than as the intermittent, render-order-dependent crash it
  // would otherwise have become.
  const drillPo = useCallback((po) => {
    if (po?.documentNumber) setPoDoc(po.documentNumber);
  }, []);

  const drillPartnerSegment = useCallback((info) => {
    if (!departmentId) return;
    const partner = info?.vendor;
    const realPartner = partner && !PARTNER_RESIDUALS.has(partner);
    setDrill({
      title: realPartner
        ? `${partner} · ${info.category}`
        : `${info?.category} · all resellers`,
      contextLabel: dateRange?.startDate
        ? `${account} · ${PANW} · ${dateRange.startDate} – ${dateRange.endDate}`
        : `${account} · ${PANW} · all time`,
      expected: realPartner ? info.spend : null,
      queryKey: ['accounts-drill-partner', departmentId, partner, info?.category],
      path: `/api/vendor-analysis/${encodeURIComponent(PANW)}/transactions`,
      params: {
        department: departmentId,
        ...(realPartner ? { supplier: partner } : {}),
        securityCategory: info?.category,
        // Same window the segment was measured over, or the list would not
        // reconcile to the segment.
        ...(dateRange?.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange?.endDate ? { endDate: dateRange.endDate } : {}),
        limit: 500,
      },
    });
  }, [departmentId, account, dateRange]);

  // The whole component used to return early on the COVERAGE query — so a slow
  // or failing /gap-analysis/coverage call put "Loading account stats…" (or a
  // coverage error) inside the Spend, Palo Alto, Security, Partners and
  // Purchasing-authority widgets, none of which read coverage. One query's
  // problem, five widgets lying about it. The early return now belongs to the
  // stats section alone; every other section answers for its own query below.
  // Hooks for Key numbers live ABOVE the section gate's early returns.
  /*
   * ONE name per fiscal year, and it is the year it ENDS in (Ryan,
   * 2026-09-16). This was a local `FY${fy-1}-${fy}` that shadowed
   * formatFiscalYear from @techstack/core — invariant 8 says to label FY
   * buckets with the shared helper for exactly this reason. The two
   * disagreed on screen: the same year read "FY25" on the footprint heatmap
   * and the bump chart, and "FY24-25" on the spend trajectory, while
   * "FY25-26" beside them meant a DIFFERENT year again.
   */
  const fyLabelOf = (fy) => formatFiscalYear(fy);
  const panwByFy = useMemo(() => {
    const by = new Map();
    for (const c of footprintQuery.data?.cells || []) {
      if (c?.fy == null) continue;
      by.set(c.fy, (by.get(c.fy) || 0) + (Number(c.spend) || 0));
    }
    return [...by.entries()].sort((a, b) => a[0] - b[0]).map(([fy, spend]) => ({ fy, spend }));
  }, [footprintQuery.data]);

  /*
   * The trajectory bar, broken into who it was paid to. A single-colour bar
   * answers "how much" and hides "of what": CDT's FY25-26 is $2.12B and
   * $1.22B of it is one Microsoft agreement — the thing a rep most needs to
   * know about that year, and the thing the chart could not say.
   *
   * Segments come from the SAME matview row set as the year total, so they
   * close the bar by construction rather than by arithmetic against a figure
   * fetched elsewhere. "Everyone else" is the remainder, never a sum of what
   * we happened to fetch, and unattributed spend is its own band rather than
   * being quietly folded into it.
   */
  const spendMix = useMemo(() => {
    const d = mixQuery.data;
    if (!d || !Array.isArray(d.points) || !d.points.length || !Array.isArray(d.byYear)) return null;
    const ranked = new Map();
    for (const p of d.points) ranked.set(p.vendor, (ranked.get(p.vendor) || 0) + (p.spend || 0));
    const top = [...ranked.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([v]) => v);
    const keyOf = (v) => `v${top.indexOf(v)}`;

    const rows = d.byYear.map((y) => {
      const row = { fy: fyLabelOf(y.year), year: y.year, total: y.total };
      let named = 0;
      for (const v of top) row[keyOf(v)] = 0;
      for (const p of d.points) {
        if (p.year !== y.year || !top.includes(p.vendor)) continue;
        row[keyOf(p.vendor)] += p.spend || 0;
        named += p.spend || 0;
      }
      row.unattributed = y.unattributed || 0;
      // Never negative: a rounding difference must not draw a band below zero.
      row.other = Math.max(0, y.total - named - (y.unattributed || 0));
      return row;
    });

    const segments = [
      ...top.map((v, i) => ({ key: `v${i}`, label: v, color: PARTNER_PALETTE[i % PARTNER_PALETTE.length] })),
      { key: 'other', label: 'Everyone else', color: PARTNER_OTHER_COLOR },
      { key: 'unattributed', label: 'No vendor resolved', color: 'var(--color-border, #d1d5db)' },
    ];
    return { rows, segments };
  }, [mixQuery.data]);

  if (section === null || section === 'stats') {
    if (coverageQuery.isLoading && section === null) {
      return <div className="accounts-panel-note">Loading account stats…</div>;
    }
    if (coverageQuery.error && section === null) {
      return <PanelError message={coverageQuery.error.message || 'Failed to load account stats'} />;
    }
  }

  const row = Array.isArray(coverageQuery.data) ? coverageQuery.data[0] : null;

  const anyTrend = spendTrend.length > 0 || panTrend.length > 0;

  /*
   * Key numbers, rebuilt (W4). "$5.7B / 31,906 / 137" was three bare numerals:
   * no trend, no baseline, no share, nothing clickable — and a number with
   * nothing to compare it against cannot say whether $5.7B is a lot for this
   * agency, or rising. Records and months-of-data are PROVENANCE, not
   * statistics; they moved into the data-window footnote and their slots went
   * to the two comparisons a rep actually uses.
   */
  const spendSeries = spendTrend.map((d) => Number(d.total) || 0);

  /*
   * Compare COMPLETE fiscal years only. A year still being lived through is
   * two months of spend next to twelve, and dividing one by the other is not a
   * trend — it is a calendar artefact wearing a red down-arrow (see
   * `isPartialFy`). When the newest bucket is in progress we step back one and
   * compare the two most recent finished years instead, and name both, because
   * "vs FY25-26" no longer implies which year is being measured.
   *
   * The partial year stays OUT of the sparkline for the same reason: plotted,
   * it draws a cliff at the right edge that means nothing. It is reported
   * separately, as a figure with its own caveat.
   */
  const partialFy = spendTrend.length > 0
    && isPartialFy(spendTrend[spendTrend.length - 1].fy);
  const completeTrend = partialFy ? spendTrend.slice(0, -1) : spendTrend;
  const completeSeries = completeTrend.map((d) => Number(d.total) || 0);

  const priorFy = completeSeries.length >= 2 ? completeSeries[completeSeries.length - 2] : null;
  const latestFy = completeSeries.length >= 1 ? completeSeries[completeSeries.length - 1] : null;
  const fyDeltaOf = completeTrend.length >= 1 ? completeTrend[completeTrend.length - 1]?.fy : null;
  const fyDeltaFrom = completeTrend.length >= 2 ? completeTrend[completeTrend.length - 2]?.fy : null;
  const partialFyLabel = partialFy ? spendTrend[spendTrend.length - 1]?.fy : null;
  const partialFyTotal = partialFy ? spendSeries[spendSeries.length - 1] : null;
  /*
   * Key numbers, rebuilt again (Ryan, 2026-09-14): "these are not really
   * useful numbers". Ten years of total spend says nothing about now; the
   * Palo Alto tile read $0 from the supplier path; months and records are
   * provenance. The three that replaced them are the three a rep opens the
   * account for — the year-over-year IT spend, the year-over-year Palo Alto
   * spend on the PRODUCT path (the footprint's own data), and how many
   * security categories are still open to us.
   */
  const today = new Date().toISOString().slice(0, 10);
  const fyIsComplete = (fy) => {
    try { return fiscalCalendar ? fiscalYearRange(fy, fiscalCalendar).end < today : true; } catch { return true; }
  };
  /*
   * The Palo Alto series sits on the SAME axis as IT spend: every completed
   * fiscal year the agency has, zero-filled. A year with no Palo Alto lines
   * is a $0 year, not a missing one — comparing "the last year with data"
   * against "the one before that" said "▼27% vs FY21-22" across a four-year
   * gap, and hid that the year in between was zero.
   */
  const allAxisFys = completeTrend.map((d) => fyEndYear(d.fy)).filter((n) => n != null);
  /*
   * ZERO-FILL ONLY INSIDE THE WINDOW WE ASKED FOR.
   *
   * The agency series above comes from /evidence, which sends NO time filter,
   * so its axis runs back years further than the footprint now does. Zero-
   * filling Palo Alto across the whole of that axis turns "this year is
   * outside the filter" into "Palo Alto spent $0 that year" — a stated figure,
   * drawn as a bar, and wrong. On DGS at 3y it claimed $0 for FY21 and FY22;
   * FY22 in fact holds $84.
   *
   * The zero-fill itself is right and stays: a year INSIDE the window with no
   * Palo Alto lines really is a $0 year, and saying so is the point (see the
   * comment above). It is only years the window excludes that must be absent
   * rather than zero.
   */
  const windowFirstFy = panwWindow.startDate
    ? fiscalYearOf(panwWindow.startDate, fiscalCalendar) : null;
  const windowLastFy = panwWindow.endDate
    ? fiscalYearOf(panwWindow.endDate, fiscalCalendar) : null;
  const axisFys = allAxisFys.filter((fy) => (windowFirstFy == null || fy >= windowFirstFy)
    && (windowLastFy == null || fy <= windowLastFy));
  const panwMap = new Map(panwByFy.map((d) => [d.fy, d.spend]));
  const panwComplete = (axisFys.length
    ? axisFys.map((fy) => ({ fy, spend: panwMap.get(fy) || 0 }))
    : panwByFy.filter((d) => fyIsComplete(d.fy)));
  const panwLatest = panwComplete.length >= 1 ? panwComplete[panwComplete.length - 1] : null;
  const panwPrior = panwComplete.length >= 2 ? panwComplete[panwComplete.length - 2] : null;
  const panwMax = panwComplete.reduce((m, d) => Math.max(m, d.spend), 0);
  const panwChange = panwLatest && panwPrior
    ? describeDelta(panwLatest.spend, panwPrior.spend, fyLabelOf(panwPrior.fy), panwMax, fmtMoney, fyLabelOf(panwLatest.fy))
    : null;
  const panwPartial = panwByFy.find((d) => !fyIsComplete(d.fy)) || null;

  /*
   * "FULL YEAR" HAS TO MEAN THE FULL YEAR.
   *
   * This card reads the FOOTPRINT's query so the two always agree, which is
   * right — but that query carries the topbar's window, and a window clips the
   * years at its edges. At 1y on CA State Library the card read "FY26 full
   * year $112K" when FY26's actual total is $140K: $112K is the slice of FY26
   * inside a twelve-month window. Worse, the IT spend card beside it reads an
   * UNFILTERED series, so the identical phrase meant a real year on the left
   * and a clipped one on the right.
   *
   * The delta is already safe — it needs two complete years and quietly
   * disappears when the window cannot supply them (observed at 1y). So only
   * the wording is wrong, and only when the window actually cuts the year.
   */
  const windowCoversFy = (fy) => windowCoversFiscalYear(fy, panwWindow, fiscalCalendar, fiscalYearRange);
  const panwLatestWhole = panwLatest ? windowCoversFy(panwLatest.fy) : true;

  /**
   * The Palo Alto card and the IT spend card beside it can name DIFFERENT
   * fiscal years, and that difference carries real information the reader
   * cannot otherwise see.
   *
   * IT spend shows the open year when there is one ("FY26-27 to date").
   * Palo Alto falls back to the latest COMPLETE year whenever the PANW series
   * has no row for the open one. Measured on DGS 2026-09-16: IT spend read
   * "$2.0M · FY26-27 to date" while Palo Alto read "$0 · FY25-26" — two cards,
   * side by side, silently scoped a year apart. Read quickly that says "Palo
   * Alto is zero"; what it actually says is "we have nothing recorded for Palo
   * Alto this year", which is a different claim and the more useful one.
   *
   * So when the two disagree, the card says so rather than leaving the year
   * label to be noticed.
   */
  const panwMissingOpenFy = Boolean(
    partialFyLabel && !panwPartial
    /*
     * COMPARE THE YEAR, NOT THE LABEL — the two sides are formatted by
     * different deploys.
     *
     * `partialFyLabel` arrives from the SERVER (`/evidence`, formatted by
     * `formatFY` in routes/v1.js). `panwByFy` carries a raw numeric fy that
     * this file formats CLIENT-side. Matching them as strings held only while
     * both spelled a fiscal year the same way, and #250 changed that spelling
     * on both sides at once — but the frontend ships via Vercel and the
     * backend via Render, independently, and `/evidence` answers
     * `public, max-age=300`, so a browser can hold a span-format body for five
     * minutes AFTER the backend has caught up. Through that window
     * 'FY27' !== 'FY26-27', the test is unanimously false, and the card states
     * "nothing recorded in FY26-27" — with a tooltip asserting no Palo Alto
     * product exists for that year — about a year that has rows.
     *
     * `fyEndYear` reads either spelling and yields the number, which is what
     * panwByFy already holds. There is no version of this that can skew.
     */
    && !panwByFy.some((d) => d.fy === fyEndYear(partialFyLabel)),
  );
  const itChange = describeDelta(latestFy, priorFy, fyDeltaFrom, Math.max(...completeSeries, 0), fmtMoney, fyDeltaOf);

  /*
   * A ratio over ONE window, never a mean of yearly ratios: averaging the
   * percentages would weight CDT's $14K year the same as its $7.5M one and
   * report 28% where the money says 21%.
   */
  const securityShare = (() => {
    const d = shareQuery.data;
    if (!d || !Array.isArray(d.series) || !d.series.length) return null;
    return d;
  })();


  // On a page that scrolls, a fixed chart height is right. In a board cell it
  // is the bug: the card grows and the drawing doesn't.
  //
  // Passed in, never inferred from `section`. Inferring it read "mounted as a
  // section" as "mounted on a board", which held only while the board was the
  // only caller — the Procurement tab mounts the spend section too, and there
  // a 100%-height chart resolves against a parent with no definite height and
  // collapses to nothing.
  const chartFill = !!fill;

  /*
   * Said once, rendered either in the card heading (off the board) or as a
   * foot caption under the chart (on it).
   *
   * Naming the span is not padding. This chart deliberately does NOT take the
   * topbar's window — a trajectory clipped to one year is not a trajectory —
   * but it sits beside a heatmap captioned "last 1y", and without this nothing
   * on its face said the two were answering different questions. A reader who
   * narrows the filter and sees the same bars is entitled to conclude the
   * filter is broken.
   *
   * IT NAMES THE BARS IT DREW, rather than asserting a span. This said "every
   * year on file" from 2026-09-17 until the same day, and that was false:
   * `/vendor-trajectory` defaults to FIVE fiscal years, and FTB has TWELVE on
   * file — FY16 through FY27, with FY21's $57.0M and FY17's $52.3M among the
   * seven the chart does not draw. The caption told the reader nothing was
   * missing. A constant in a caption is a claim nobody re-checks when the
   * query underneath it changes; a range read off the rendered rows cannot go
   * stale, which is the whole reason this is computed and not typed.
   */
  const drawnRows = spendMix ? spendMix.rows : spendTrend;
  const drawnSpan = fiscalSpanLabel(drawnRows);
  const spanNote = drawnSpan ? `${drawnSpan}, not the time filter` : 'not the time filter';
  const scopeNote = `${spendMix ? 'classified spend by vendor' : 'total spend'} · California state fiscal year · ${spanNote}${departmentId ? ' · click a bar for transactions' : ''}`;
  const panScopeNote = `Palo Alto spend by fiscal year${departmentId ? ' · click a bar for transactions' : ''}`;

  // The delegated per-transaction ceiling the gauge draws its line at.
  //
  // `itCeiling` is the catalog's headline field and has THREE shapes, not two:
  // a number, the string 'unlimited', or null. Only the number is a line;
  // 'unlimited' and the two unlisted outcomes each mean something different to
  // a rep deciding who signs, so the gauge is told which rather than being
  // handed a falsy value and left to guess.
  const pa = paQuery.data?.purchasingAuthority;
  const rawCeiling = pa?.itCeiling;
  const ceilingValue = Number.isFinite(Number(rawCeiling)) && Number(rawCeiling) > 0
    ? Number(rawCeiling)
    : null;
  const noCeilingNote = rawCeiling === 'unlimited'
    ? 'this tier has no dollar cap'
    : pa?.status === 'on_request'
      ? 'DGS lists them but does not publish the tier'
      : pa?.status === 'not_listed'
        ? 'not on the DGS delegation list'
        : 'no published ceiling for this entity';


  /**
   * What this ONE section has to say when it has no content to draw.
   *
   * On a page that stacks panels, rendering nothing is right — the panel just
   * isn't there. On a board the cell is already drawn, so rendering nothing is
   * a titled card with a blank body, which reads as broken rather than empty.
   * Every section therefore answers loading / error / empty for its OWN query.
   */
  const sectionStatus = () => {
    switch (section) {
      case 'stats':
        if (coverageQuery.isLoading) return { state: 'loading' };
        if (coverageQuery.error) {
          return {
            state: 'error',
            message: coverageQuery.error.message || 'Account stats unavailable.',
            onRetry: () => coverageQuery.refetch(),
          };
        }
        if (!row) {
          return { state: 'empty', message: 'No procurement coverage recorded for this agency yet.' };
        }
        return null;
      case 'spend':
        if (trendQuery.isLoading) return { state: 'loading' };
        if (trendQuery.error) {
          return {
            state: 'error',
            message: `Spend trajectory unavailable — ${trendQuery.error.message}`,
            onRetry: () => trendQuery.refetch(),
          };
        }
        if (spendTrend.length === 0) {
          return { state: 'empty', message: 'No recorded spend for this agency.' };
        }
        return null;
      /*
       * No `case 'pan'`. The PANW trajectory is no longer mounted as a widget
       * on TekStak — `panw-heatmap` replaced it, because this path resolves
       * Palo Alto by SUPPLIER and Palo Alto has no supplier rows in California
       * procurement, so its empty state read "No Palo Alto spend recorded at
       * this agency" at an agency holding $13.2M of Palo Alto product.
       *
       * `panwQuery` and `panTrend` stay: the Key numbers tile still reads them
       * for its Palo Alto figure. That tile has the SAME defect and is not
       * fixed here — it is reported separately rather than smuggled into a
       * heatmap change.
       */
      case 'security':
        if (securityQuery.isLoading) return { state: 'loading' };
        if (securityQuery.error) {
          return {
            state: 'error',
            message: `Security coverage unavailable — ${securityQuery.error.message}`,
            onRetry: () => securityQuery.refetch(),
          };
        }
        if (!security) {
          return { state: 'empty', message: 'No security spend recorded for this agency.' };
        }
        return null;
      case 'partners':
        if (partnersQuery.isLoading) return { state: 'loading' };
        if (partnersQuery.error) {
          return {
            state: 'error',
            message: `Partner spend unavailable — ${partnersQuery.error.message}`,
            onRetry: () => partnersQuery.refetch(),
          };
        }
        if (!partners) {
          return { state: 'empty', message: 'No partner-carried Palo Alto spend at this agency.' };
        }
        return null;
      case 'purchasing-authority':
        if (paQuery.isLoading) return { state: 'loading' };
        if (paQuery.error) {
          return {
            state: 'error',
            message: `Purchasing authority unavailable — ${paQuery.error.message}`,
            onRetry: () => paQuery.refetch(),
          };
        }
        // PurchasingAuthorityCard returns null on a missing record, so without
        // this the widget is blank for every unlisted entity.
        if (!paQuery.data?.purchasingAuthority) {
          return {
            state: 'empty',
            message: 'DGS has not published a delegated tier for this entity.',
          };
        }
        return null;
      default:
        return null;
    }
  };

  const status = section ? sectionStatus() : null;
  if (status) return <WidgetStatus {...status} />;

  return (
    <div className={section ? 'accounts-dash accounts-dash--panel' : 'accounts-dash'}>
      {/* GAS's anatomy: identity and facts first, then charts, then the
          purchasing ceiling that frames the renewals below it. On the board
          each of these is its own widget and the rep decides the order. */}
      {section === null && <AccountsOverviewIntel selectedDepartment={selectedDepartment} />}

      {wants('stats') && (
      <>
      <div className={section ? 'ts-stat-row ts-stat-row--flush' : 'ts-stat-row'}>
        {/* 1. IT spend. The figure is the year the rep is SELLING INTO when one
            is open, because that is the number they act on — CDT's Palo Alto
            card led with $1.0M for a closed year while the $8.6M EA signed in
            the open one sat underneath as a footnote. "to date" is on the
            label, never implied, and the closed year it is measured against
            keeps its own line, so the two are never mistaken for each other. */}
        <div className="ts-stat-card">
          <div className="ts-stat-card__value-row">
            <div className="ts-stat-card__value">
              {partialFyLabel ? fmtMoney(partialFyTotal) : (latestFy != null ? fmtMoney(latestFy) : '—')}
            </div>
            {completeSeries.length >= 2 && (
              <Sparkline
                values={completeSeries}
                label={`IT spend by completed fiscal year, ${completeTrend[0]?.fy} to ${fyDeltaOf}`}
              />
            )}
          </div>
          {/* The qualifier is its own span so the narrow rung can break the
              label after the name instead of letting it wrap raggedly: at
              phone width "IT spend · FY26-27 to date" took FOUR lines in a
              78px column and pushed the tile past its cell. */}
          <div className="ts-stat-card__label">
            <span className="ts-stat-card__label-name">IT spend</span>
            {(partialFyLabel || fyDeltaOf) && (
              <span className="ts-stat-card__label-qual">
                <span className="ts-stat-card__label-sep"> · </span>
                {partialFyLabel ? `${partialFyLabel} to date` : fyDeltaOf}
              </span>
            )}
          </div>
          {(itChange || (partialFyLabel && latestFy != null)) && (
            <div className="accounts-stat-trend accounts-stat-trend--wrap">
              {partialFyLabel && latestFy != null && (
                <span className="accounts-share accounts-prior" title={`${fyDeltaOf} closed at ${fmtMoney(latestFy)} — a full year, not comparable to a year in progress`}>
                  {fyDeltaOf} full year {fmtMoney(latestFy)}
                </span>
              )}
              {itChange && (
                <span className={`accounts-delta${itChange.up ? ' is-up' : ' is-down'}`} title={itChange.title}>
                  {itChange.text}
                </span>
              )}
            </div>
          )}
        </div>

        {/* 2. Palo Alto PRODUCT, latest complete fiscal year, year over year —
            the footprint's own numbers, so the two always agree. */}
        <div className="ts-stat-card">
          <div className="ts-stat-card__value-row">
            <div className="ts-stat-card__value">
              {panwPartial ? fmtMoney(panwPartial.spend) : (panwLatest ? fmtMoney(panwLatest.spend) : '—')}
            </div>
            {panwComplete.length >= 2 && (
              <Sparkline
                values={panwComplete.map((d) => d.spend)}
                label={`Palo Alto product by completed fiscal year, ${fyLabelOf(panwComplete[0].fy)} to ${fyLabelOf(panwLatest.fy)}`}
              />
            )}
          </div>
          <div className="ts-stat-card__label">
            <span className="ts-stat-card__label-name">Palo Alto</span>
            {(panwPartial || panwLatest) && (
              <span className="ts-stat-card__label-qual">
                <span className="ts-stat-card__label-sep"> · </span>
                {panwPartial ? `${fyLabelOf(panwPartial.fy)} to date` : fyLabelOf(panwLatest.fy)}
              </span>
            )}
          </div>

          <div className="accounts-stat-trend accounts-stat-trend--wrap">
            {panwPartial && panwLatest && (
              <span
                className="accounts-share accounts-prior"
                title={panwLatestWhole
                  ? `${fyLabelOf(panwLatest.fy)} closed at ${fmtMoney(panwLatest.spend)} — a full year, not comparable to a year in progress`
                  : `${fyLabelOf(panwLatest.fy)} is only partly inside the current time filter, so this is the spend in view, not the year's total. Widen the filter to see the whole year.`}
              >
                {fyLabelOf(panwLatest.fy)} {panwLatestWhole ? 'full year' : 'in view'} {fmtMoney(panwLatest.spend)}
              </span>
            )}
            {panwMissingOpenFy && (
              <span
                className="accounts-share accounts-prior"
                title={`IT spend beside this is showing ${partialFyLabel}, which is still open. No Palo Alto product is recorded against this agency in that year, so this card falls back to the last year that has any — the two cards are not the same window.`}
              >
                nothing recorded in {partialFyLabel}
              </span>
            )}
            {panwChange && (
              <span className={`accounts-delta${panwChange.up ? ' is-up' : ' is-down'}`} title={`${panwChange.title} — Palo Alto product bought by this agency`}>
                {panwChange.text}
              </span>
            )}
            {!panwByFy.length && !footprintQuery.isLoading && (
              <span className="accounts-share">No Palo Alto product on file</span>
            )}
          </div>
        </div>

        {/* 3. Our share of the agency's security spend, over five fiscal years,
            with the agency's security spend as the line behind it. The figure
            it replaced — how many categories sit open — counted doors rather
            than money, and said nothing about whether we are winning. */}
        <div className="ts-stat-card">
          <div className="ts-stat-card__value-row">
            <div className="ts-stat-card__value">
              {securityShare && securityShare.pct != null ? `${securityShare.pct.toFixed(0)}%` : '—'}
            </div>
            {securityShare && securityShare.series.length >= 2 && (
              <Sparkline
                values={securityShare.series.map((d) => d.total)}
                label={`Agency security spend by fiscal year, ${fyLabelOf(securityShare.fromFy)} to ${fyLabelOf(securityShare.toFy)}`}
              />
            )}
          </div>
          <div className="ts-stat-card__label">
            <span className="ts-stat-card__label-name">Our share of security</span>
            {securityShare && securityShare.fromFy != null && (
              <span className="ts-stat-card__label-qual">
                <span className="ts-stat-card__label-sep"> · </span>
                {`${fyLabelOf(securityShare.fromFy)}–${fyLabelOf(securityShare.toFy)}`}
              </span>
            )}
          </div>
          <div className="accounts-stat-trend accounts-stat-trend--wrap">
            {securityShare && securityShare.pct != null ? (
              <span
                className="accounts-share"
                title={`${fmtMoney(securityShare.ours)} of ${fmtMoney(securityShare.total)} of security spend across ${securityShare.years} fiscal years. Summed over the whole window rather than averaged year by year, because an enterprise agreement lands in one year and covers several.`}
              >
                {fmtMoney(securityShare.ours)} of {fmtMoney(securityShare.total)}
              </span>
            ) : (
              <span className="accounts-share">
                {shareQuery.isLoading ? '…' : (shareQuery.error ? 'Security share unavailable' : 'No security spend on file')}
              </span>
            )}
          </div>
        </div>
      </div>

      {!row && (
        <div className="accounts-panel-note">
          No procurement coverage recorded for this agency yet.
        </div>
      )}
      </>
      )}

      {/* Per-view control, right above the bars it moves — not buried in
          settings (invariant 8). Only meaningful once PANW bars exist. */}
      {panTrend.length > 0 && wants('pan') && (
        <div className="accounts-basis-row">
          <FiscalBasisToggle surface="accounts-dash" />
        </div>
      )}

      {anyTrend && (wants('spend') || wants('pan')) && (
        <div className={section ? 'accounts-chart-grid accounts-chart-grid--single' : 'accounts-chart-grid'}>
          {spendTrend.length > 0 && wants('spend') && (
            /* On the board this is frameless with its scope underneath, which
               is how the footprint beside it reads. The widget shell already
               carries the name, so an inner card and an inner heading were a
               box inside a box and the title twice. Off the board — the
               Procurement tab — the card and heading still do their job. */
            <div className={section ? 'accounts-chart-plain' : 'ts-card'}>
              {!section && (
                <h3 className="ts-card-title">
                  Procurement trajectory
                  <span className="accounts-eyebrow">
                    {scopeNote}
                  </span>
                </h3>
              )}
              <SpendTrendChart
                data={spendMix ? spendMix.rows : spendTrend}
                segments={spendMix ? spendMix.segments : undefined}
                height={220}
                fill={chartFill}
                accentColor="#64748b"
                onBarClick={departmentId ? drillAgencyBar : undefined}
              />
              {section && <p className="ts-foot">{scopeNote}</p>}
            </div>
          )}
          {panTrend.length > 0 && wants('pan') && (
            <div className={section ? 'accounts-chart-plain' : 'ts-card'}>
              {!section && (
              <h3 className="ts-card-title">
                Palo Alto trajectory
                <span className="accounts-eyebrow">
                  {panScopeNote}
                </span>
              </h3>
              )}
              <SpendTrendChart
                data={panTrend}
                height={220}
                fill={chartFill}
                onBarClick={departmentId ? drillPanwBar : undefined}
              />
              {section && <p className="ts-foot">{panScopeNote}</p>}
            </div>
          )}
        </div>
      )}

      {trendQuery.error && wants('spend') && (
        <PanelError message={`Spend trajectory unavailable — ${trendQuery.error.message}`} />
      )}
      {panwQuery.error && wants('pan') && (
        <PanelError message={`Palo Alto trajectory unavailable — ${panwQuery.error.message}`} />
      )}

      {wants('purchasing-authority') && (
        <>
          <PurchasingAuthorityCard purchasingAuthority={paQuery.data?.purchasingAuthority} />
          {/* The gauge is the picture of what the card above states in text:
              their real POs against the ceiling it names. Hidden at the glance
              rung, where there is no width to read a scatter. */}
          {poQuery.data?.purchaseOrders?.length > 0 && (
            <div className="accounts-gauge cq-hide-glance">
              <CeilingGauge
                purchaseOrders={poQuery.data.purchaseOrders}
                ceiling={ceilingValue}
                noCeilingNote={noCeilingNote}
                ranked={poQuery.data.ranked}
                sinceYear={poQuery.data.sinceYear}
                fill={chartFill}
                onPick={drillPo}
              />
            </div>
          )}
          {poQuery.error && (
            <p className="accounts-panel-note">
              {poQuery.error.code === 'AGGREGATE_TIMEOUT'
                ? 'The purchase-order totals took too long for this agency.'
                : `Purchase orders unavailable — ${poQuery.error.message}`}
            </p>
          )}
        </>
      )}

      {security && wants('security') && (
        <div className="ts-card accounts-heatmap-card">
          <h3 className="ts-card-title">
            Security coverage
            <span className="accounts-eyebrow">
              top vendors × category
              {/* Say the window on the chart's face. The whole complaint was
                  not knowing it was all-time; a widget that silently changes
                  what it covers when the topbar moves has just relocated the
                  same problem. */}
              {timePeriod && timePeriod !== 'all' ? ` · last ${timePeriod}` : ' · all time'}
              {departmentId ? ' · click a cell for transactions' : ''}
            </span>
          </h3>
          <SecurityCoverageHeatmap
            rows={security.rows}
            /* The rows are VENDORS here, not agencies — the default said Agency
               over Palo Alto Networks, Salesforce and Okta. */
            rowHeader="Vendor"
            columns={security.columns}
            cells={security.cells}
            maxCellSpend={security.maxCell}
            colorMode="log"
            showValues
            maxHeight={360}
            fill={chartFill}
            onCellClick={departmentId ? drillSecurityCell : undefined}
          />
        </div>
      )}
      {securityQuery.error && wants('security') && (
        <PanelError message={`Security coverage unavailable — ${securityQuery.error.message}`} />
      )}

      {partners && wants('partners') && (
        <div className="ts-card accounts-partners-card">
          <h3 className="ts-card-title">
            Partner spend by security category
            {/* Names the axis, because Partner involvement sits beside this on
                the Partners tab stacking the SAME money the other way round —
                bar per partner, segment per category. Two widgets, colour
                meaning two different things, and previously nothing on either
                one saying which. Its legend now leads with the same phrasing. */}
            <span className="accounts-eyebrow">
              bar = security category · segments = partner
              {departmentId ? ' · click a segment for transactions' : ''}
            </span>
          </h3>
          <CategorySpendBars
            data={partners.chartData}
            topVendors={partners.topVendors}
            colorMap={partners.colorMap}
            height={440}
            fill={chartFill}
            maxBarSize={72}
            onSegmentClick={departmentId ? drillPartnerSegment : undefined}
          />
        </div>
      )}
      {partnersQuery.error && wants('partners') && (
        <PanelError message={`Partner spend unavailable — ${partnersQuery.error.message}`} />
      )}

      {poDoc && (
        <PoDetailModal
          documentNumber={poDoc}
          departmentId={departmentId}
          isOpen
          onClose={() => setPoDoc(null)}
        />
      )}

      <SharedTransactionsDrilldownModal
        isOpen={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        contextLabel={drill?.contextLabel}
        queryKey={drill?.queryKey || ['accounts-drill-idle']}
        queryFn={drill ? () => apiClient.get(drill.path, { params: drill.params }) : undefined}
        footerNote={drill?.expected != null
          ? `Chart figure: ${fmtMoney(drill.expected)} — the list below should reconcile to it.`
          : null}
      />
    </div>
  );
}
